import { useEffect, useRef } from 'react'
import { useToast } from '../components/ToastProvider'
import {
  PromptLibraryListSection,
  PromptLibraryOverlays,
  PromptManagementShell,
  usePromptLibraryData,
  usePromptLibraryDialogs,
  usePromptLibraryPlayback,
  usePromptPoolManagement,
} from '../features/suno/PromptManagement'

export const PromptLibraryPage = () => {
  const { toast } = useToast()
  const promptGroupFilterRef = useRef<HTMLDetailsElement | null>(null)

  const {
    promptSearch,
    setPromptSearch,
    promptGroupFilter,
    promptSortBy,
    promptSortDirection,
    promptPage,
    promptPageSize,
    promptTotal,
    sunoPrompts,
    promptsLoading,
    promptsError,
    sunoPromptGroups,
    promptSaving,
    promptDeleting,
    clearPromptSearch,
    confirmDeletePrompt,
    loadSunoPromptGroups,
    loadSunoPrompts,
    savePrompt,
    selectPromptGroup,
    submitPromptSearch,
    suggestPromptMetadata,
    togglePromptSort,
    updatePromptPage,
    updatePromptPageSize
  } = usePromptLibraryData({ toast })

  const {
    promptEditor,
    promptToDelete,
    promptTracksDrawerPrompt,
    promptSimilarityPrompt,
    promptActionMenuId,
    promptActionMenuPosition,
    closePromptActionMenu,
    closePromptDeleteDialog,
    closePromptEditor,
    closePromptSimilarity,
    closePromptTracksDrawer,
    copyText,
    openCreatePromptDialog,
    openDeletePromptDialog,
    openEditPromptDialog,
    openPromptSimilarity,
    openPromptTracksDrawer,
    setPromptEditor,
    togglePromptActionMenu
  } = usePromptLibraryDialogs({ toast })

  const {
    playingTrack: promptPlayingTrack,
    audioLoading: promptAudioLoading,
    audioPlaying: promptAudioPlaying,
    audioCurrentTime: promptAudioCurrentTime,
    audioDuration: promptAudioDuration,
    audioMuted: promptAudioMuted,
    audioVolume: promptAudioVolume,
    togglePlayback: promptTogglePlayback,
    stopPlayback: promptStopPlayback,
    seekPlayback: promptSeekPlayback,
    updateAudioVolume: promptUpdateAudioVolume,
    toggleAudioMute: promptToggleAudioMute,
    playbackTracks: promptPlaybackTracks,
    playbackPromptId: promptPlaybackPromptId,
    playbackLoadingId: promptPlaybackLoadingId,
    playPromptTracks,
    playPromptNext
  } = usePromptLibraryPlayback()

  const {
    dissolvePromptPool,
    expandedPromptPoolAnchorPromptIds,
    expandedPromptPoolIds,
    openPromptPoolEditor,
    openPromptPoolExpansion,
    promptPoolDetailsById,
    promptPoolEditor,
    promptPoolErrorsById,
    promptPoolSaving,
    refreshExpandedPromptPool,
    removePromptPoolVariant,
    savePromptPool,
    setPromptPoolCanonical,
    setPromptPoolEditor,
    togglePromptPoolExpansion
  } = usePromptPoolManagement({
    refreshPrompts: loadSunoPrompts,
    toast
  })

  useEffect(() => {
    void loadSunoPromptGroups()
    void loadSunoPrompts()
  }, [loadSunoPromptGroups, loadSunoPrompts])

  useEffect(() => {
    const closePromptGroupFilter = (event: PointerEvent): void => {
      const dropdown = promptGroupFilterRef.current
      const target = event.target

      if (!dropdown || !(target instanceof Node) || dropdown.contains(target)) {
        return
      }

      dropdown.open = false
    }

    document.addEventListener('pointerdown', closePromptGroupFilter)

    return () => {
      document.removeEventListener('pointerdown', closePromptGroupFilter)
    }
  }, [])

  return (
    <>
      <PromptManagementShell actionLabel="Create prompt" onAction={openCreatePromptDialog}>
        <PromptLibraryListSection
          actionMenuId={promptActionMenuId}
          actionMenuPosition={promptActionMenuPosition}
          audioLoading={promptAudioLoading}
          audioPlaying={promptAudioPlaying}
          detailsRef={promptGroupFilterRef}
          error={promptsError}
          expandedPromptPoolAnchorPromptIds={expandedPromptPoolAnchorPromptIds}
          expandedPromptPoolIds={expandedPromptPoolIds}
          groups={sunoPromptGroups}
          isLoading={promptsLoading}
          items={sunoPrompts}
          loadingPromptId={promptPlaybackLoadingId}
          onClearSearch={clearPromptSearch}
          onCloseActionMenu={closePromptActionMenu}
          onCopyText={copyText}
          onDeletePrompt={openDeletePromptDialog}
          onDissolvePromptPool={dissolvePromptPool}
          onEditPrompt={openEditPromptDialog}
          onGroupChange={selectPromptGroup}
          onNextPage={() => updatePromptPage(Math.min(Math.ceil(promptTotal / promptPageSize), promptPage + 1))}
          onOpenPromptPoolEditor={openPromptPoolEditor}
          onOpenSimilarity={openPromptSimilarity}
          onOpenTracks={openPromptTracksDrawer}
          onPageSizeChange={updatePromptPageSize}
          onPlayPromptTracks={playPromptTracks}
          onPreviousPage={() => updatePromptPage(Math.max(1, promptPage - 1))}
          onRemovePromptPoolVariant={removePromptPoolVariant}
          onSearchChange={setPromptSearch}
          onSetPromptPoolCanonical={setPromptPoolCanonical}
          onSort={togglePromptSort}
          onSubmit={submitPromptSearch}
          onToggleActionMenu={togglePromptActionMenu}
          onTogglePromptPoolExpansion={togglePromptPoolExpansion}
          page={promptPage}
          pageSize={promptPageSize}
          playbackPromptId={promptPlaybackPromptId}
          playbackTracks={promptPlaybackTracks}
          playingTrack={promptPlayingTrack}
          poolDetailsById={promptPoolDetailsById}
          poolErrorsById={promptPoolErrorsById}
          search={promptSearch}
          selectedGroupId={promptGroupFilter}
          sortBy={promptSortBy}
          sortDirection={promptSortDirection}
          total={promptTotal}
        />
      </PromptManagementShell>

      <PromptLibraryOverlays
        audioCurrentTime={promptAudioCurrentTime}
        audioDuration={promptAudioDuration}
        audioLoading={promptAudioLoading}
        audioMuted={promptAudioMuted}
        audioPlaying={promptAudioPlaying}
        audioVolume={promptAudioVolume}
        groups={sunoPromptGroups}
        loadingPromptId={promptPlaybackLoadingId}
        onChanged={() => void loadSunoPrompts()}
        onClosePromptDelete={closePromptDeleteDialog}
        onClosePromptEditor={closePromptEditor}
        onClosePromptPoolEditor={() => setPromptPoolEditor(null)}
        onClosePromptSimilarity={closePromptSimilarity}
        onClosePromptTracks={closePromptTracksDrawer}
        onConfirmDeletePrompt={confirmDeletePrompt}
        onOpenPool={openPromptPoolExpansion}
        onPlayPromptTracks={playPromptTracks}
        onPromptEditorChange={setPromptEditor}
        onPromptPoolEditorChange={setPromptPoolEditor}
        onPromptPoolSave={(input) => void savePromptPool(input)}
        onPromptSave={savePrompt}
        onSeek={promptSeekPlayback}
        onStopPlayback={promptStopPlayback}
        onSuggestPromptMetadata={suggestPromptMetadata}
        onToggleMute={promptToggleAudioMute}
        onTogglePlayback={promptTogglePlayback}
        onVolumeChange={promptUpdateAudioVolume}
        playbackPromptId={promptPlaybackPromptId}
        playbackTracks={promptPlaybackTracks}
        playingTrack={promptPlayingTrack}
        promptDeleting={promptDeleting}
        promptEditor={promptEditor}
        promptPoolEditor={promptPoolEditor}
        promptPoolSaving={promptPoolSaving}
        promptSaving={promptSaving}
        promptSimilarityPrompt={promptSimilarityPrompt}
        promptToDelete={promptToDelete}
        promptTracksDrawerPrompt={promptTracksDrawerPrompt}
        {...(promptPlaybackTracks.length > 1 ? { onNextTrack: playPromptNext } : {})}
      />
    </>
  )
}
